import React, { useRef } from 'react';
import AddIcCallIcon from '@mui/icons-material/AddIcCall';
import DialpadIcon from '@mui/icons-material/Dialpad';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import PauseIcon from '@mui/icons-material/Pause';
import { AccountGroupOutline } from 'mdi-material-ui';

import { ContactEntity } from '~/components/Contact';
import Button from '~/components/core/Atomic/Buttons/Button';
import Grid from '~/components/core/Atomic/Grid/Grid';
import Tooltip from '~/components/core/Atomic/Tooltip';
import Typography from '~/components/core/Atomic/Typography';
import type { PhoneCallCommunication, PhoneCallConferenceParticipant } from '~/server_shared/swagger/data-contracts';
import cn from '~/Utils/cn';

import { FormikPhoneCallContentTextField } from './PhoneCallCommunicationCard/PhoneCallCommunicationCard';
import AdditionalConferenceParticipants from './AdditionalConferenceParticipants';
import DialPadCollapsableCard from './DialPadCollapsableCard';
import PhoneCallRecordingToggle from './PhoneCallRecordingToggle';

const BUTTON_CLASS = 'ml-12 h-40 w-40 min-w-40 rounded-[50%] border-none bg-blue-600 p-8';
const BUTTON_ICON_CLASS = 'text-3xl text-white';

interface Props {
  callStatus?: string;
  onHangup: () => void;
  onToggleMute: (mute: boolean) => void;
  onClickAddParticipant: () => void;
  communication: PhoneCallCommunication;
  setCommunication: React.Dispatch<React.SetStateAction<PhoneCallCommunication>>;
  participantsDetails: PhoneCallConferenceParticipant[];
  isMuted?: boolean;
  onClickPutOnHold: () => void;
  isOnHold?: boolean;
  isUpdatingHoldStatus?: boolean;
  callDurationSec?: number;
  currentlyClickedDigits?: string;
  onClickDigit: (digit: string) => void;
  isNew?: boolean;
  isDocument?: boolean;
  isEditing?: boolean;
  onRequestEdit?: () => void;
}

const NewPhoneCallCommunicationSpecificBody = (props: Props) => {
  const {
    communication,
    setCommunication,
    participantsDetails,
    callStatus,
    onHangup,
    onToggleMute,
    isOnHold,
    isUpdatingHoldStatus,
    onClickPutOnHold,
    isMuted,
    onClickAddParticipant,
    currentlyClickedDigits,
    onClickDigit,
    isNew,
    isDocument,
    isEditing,
    onRequestEdit,
  } = props;
  const isCallActive = callStatus === 'open';
  const [showDialPadOpen, setShowDialPadOpen] = React.useState<boolean>(false);
  const cacheKey = useRef(Date.now());

  const isView = !isNew && !isDocument;

  return (
    <>
      {isView ? (
        <>
          {communication.recording_stored_file_id && (
            <div>
              <audio controls src={`/api/v1/calls/${communication.id}/recording?nocache=${cacheKey.current}`} />
            </div>
          )}

          {communication.additional_conference_participants &&
          communication.additional_conference_participants.length !== 0 ? (
            <>
              <div style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap' }}>
                <AccountGroupOutline />
                &nbsp;
                <Typography display="inline" variant="subtitle1">
                  Conference Call:
                </Typography>
              </div>
              {communication.additional_conference_participants.map((participant) => (
                <Grid key={participant.id} item xs={12}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap' }}>
                    {participant.contact_id ? (
                      <ContactEntity
                        contactId={participant.contact?.id}
                        contactDisplayName={participant.contact?.full_name}
                      />
                    ) : (
                      <Typography display="inline">
                        {participant.name} &nbsp;-&nbsp; {participant.phone_number}
                      </Typography>
                    )}
                    {participant.status !== 'Off' && <span>&nbsp;({participant.status})</span>}{' '}
                    {/* Call was not answered */}
                  </div>
                </Grid>
              ))}
            </>
          ) : null}
        </>
      ) : null}
      {isCallActive ? (
        <>
          <div className="pt-10 flex items-center justify-between">
            <div className="flex items-center gap-40">
              <PhoneCallRecordingToggle
                communication={communication}
                setCommunication={setCommunication}
                buttonOnly={true}
              />
              <div className="flex items-center">
                <Tooltip title={isMuted ? 'Unmute' : 'Mute'}>
                  {isMuted ? (
                    <Button
                      variant="contained"
                      className={cn(BUTTON_CLASS, 'bg-blue-500')}
                      onClick={() => onToggleMute(false)}
                    >
                      <MicOffIcon className={BUTTON_ICON_CLASS} />
                    </Button>
                  ) : (
                    <Button variant="contained" className={BUTTON_CLASS} onClick={() => onToggleMute(true)}>
                      <MicIcon className={BUTTON_ICON_CLASS} />
                    </Button>
                  )}
                </Tooltip>
                <Tooltip title="Add Call Participant">
                  <Button
                    variant="contained"
                    onClick={onClickAddParticipant}
                    disabled={isUpdatingHoldStatus}
                    className={BUTTON_CLASS}
                  >
                    <AddIcCallIcon className={cn(BUTTON_ICON_CLASS, 'mr-2 mt-2')} />
                  </Button>
                </Tooltip>
                <Tooltip title="Dial Pad">
                  <Button
                    className={cn(BUTTON_CLASS, { 'bg-teal-600': showDialPadOpen })}
                    variant="contained"
                    color="primary"
                    onClick={() => setShowDialPadOpen(!showDialPadOpen)}
                    disabled={isUpdatingHoldStatus}
                  >
                    <DialpadIcon className={BUTTON_ICON_CLASS} />
                  </Button>
                </Tooltip>
                <Tooltip title={isOnHold ? 'Remove Participants From Hold' : 'Put Participants On Hold'}>
                  <Button
                    variant="contained"
                    className={cn(BUTTON_CLASS, isOnHold ? 'bg-blue-400' : 'bg-blue-600')}
                    color={isOnHold ? 'inherit' : 'primary'}
                    disabled={isUpdatingHoldStatus}
                    onClick={onClickPutOnHold}
                  >
                    <PauseIcon className={BUTTON_ICON_CLASS} />
                  </Button>
                </Tooltip>
              </div>
            </div>
            <Button
              className="h-40 bg-red-700 py-0 pl-4 pr-12 text-white shadow-none"
              variant="contained"
              color="secondary"
              onClick={onHangup}
            >
              <div className="flex items-center">
                <div className="fs-icon-PhoneUnansweredIcon mr-4 text-3xl font-semibold text-white" />
                Hang Up
              </div>
            </Button>
          </div>

          <div className="pt-10">
            <PhoneCallRecordingToggle communication={communication} setCommunication={setCommunication} />
          </div>
          {showDialPadOpen ? (
            <DialPadCollapsableCard
              currentlyClickedDigits={currentlyClickedDigits || ''}
              onClickDigit={onClickDigit}
              onClose={() => setShowDialPadOpen(false)}
            />
          ) : null}
        </>
      ) : null}

      <AdditionalConferenceParticipants
        isCallActive={isCallActive}
        participantsDetails={participantsDetails}
        communication={communication}
      />

      <FormikPhoneCallContentTextField
        communication={communication}
        isView={isView}
        isDocument={isDocument}
        isEditing={isEditing}
        onRequestEdit={isEditing ? onRequestEdit : undefined}
      />
    </>
  );
};

export default NewPhoneCallCommunicationSpecificBody;
